import { Directive, OnInit, ViewChild } from '@angular/core';
import { SaveTypes } from 'app/models/enums/SaveTypes';
import { TaskNoteTypes } from 'app/models/enums/TaskNoteTypes';
import { WorkflowService } from '../../../../../services/workflow.service';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';
import { PageOptimizationAuditComponent } from '../shared/audit/audit.component';
import { PageOptimizationContentComponent } from '../shared/content/content.component';
import { PageOptimizationImplementationUrlComponent } from '../shared/implementation-url/implementation-url.component';
import { PageOptimizationNoteComponent } from '../shared/note/note.component';
import { PageOptimizationChecklistComponent } from '../shared/checklist/checklist.component';
import Task = Boo.Objects.Task;

@Directive()
export abstract class PageOptimizationBase implements IPersistableActionWorkspace, OnInit {
  @ViewChild(PageOptimizationAuditComponent) auditComponent: PageOptimizationAuditComponent;
  @ViewChild(PageOptimizationContentComponent) contentComponent: PageOptimizationContentComponent;
  @ViewChild(PageOptimizationImplementationUrlComponent) implementationUrlComponent: PageOptimizationImplementationUrlComponent;
  @ViewChild(PageOptimizationNoteComponent) noteComponent: PageOptimizationNoteComponent;
  @ViewChild(PageOptimizationChecklistComponent) checklistComponent: PageOptimizationChecklistComponent;

  actionViewModel: KnockoutObservable<IActionWorkspace>;
  currentAction: KnockoutObservable<Boo.Objects.Action>;
  taskDetails: Boo.Objects.ActionDetail;
  customer: Boo.Objects.Customer;
  customerUrl: string;

  isLoading = true;
  taskId: number;
  userId: number;
  websiteUrlId: number;
  workflowId: number;
  orderingTask: Task;

  TaskNoteTypes = TaskNoteTypes;

  constructor(private workflowService: WorkflowService) { }

  ngOnInit(): void {
    forkJoin([
      this.workflowService.getWorkflowIdByTaskId(this.taskId),
      this.workflowService.getOrderingTaskByTaskId(this.taskId)
    ])
      .pipe(
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: ([workflowId, orderingTask]) => {
          this.workflowId = workflowId;
          this.orderingTask = orderingTask.Task;
        },
        error: (err) => toastr.error('Failed to load:', err)
      });
  }

  canActivate?(user: Boo.Objects.User, _partner: Boo.Objects.Partner, params: IActionWorkspaceActivationParams): boolean {
    params.controlViewModel(this);
    this.currentAction = params.currentAction;
    this.actionViewModel = ko.observable(null);

    this.customer = params.currentAction().Customer;
    this.taskId = this.currentAction().Task.TaskId;
    this.userId = user.UserId;
    this.websiteUrlId = this.currentAction().Task.WebsiteUrlId;

    return true;
  }

  isStepReadyToSave(): boolean {
    return this.areComponentsValid(SaveTypes.Save);
  }

  save(): Observable<void> {
    return this.saveComponents(SaveTypes.Save);
  }

  isStepReadyToComplete(): boolean {
    return this.areComponentsValid(SaveTypes.Complete);
  }

  complete(): Observable<void> {
    return this.saveComponents(SaveTypes.Complete);
  }

  isStepReadyToReject(): boolean {
    return this.areComponentsValid(SaveTypes.Reject);
  }

  reject(): Observable<void> {
    return this.saveComponents(SaveTypes.Reject);
  }

  isStepReadyToDelete(): boolean {
    return false;
  }

  delete(): Observable<void> {
    return of(void 0);
  }

  private areComponentsValid(saveType: SaveTypes): boolean {
    const components = [this.auditComponent, this.contentComponent, this.implementationUrlComponent, this.noteComponent, this.checklistComponent].filter(Boolean);

    return components.map(c => c.isValid(saveType)).every(Boolean);
  }

  private saveComponents(saveType: SaveTypes): Observable<void> {
    const components = [this.auditComponent, this.contentComponent, this.implementationUrlComponent, this.noteComponent, this.checklistComponent].filter(Boolean);

    if (components.length === 0) {
      return of(void 0);
    }

    return forkJoin(components.map(c => c.save(saveType) || of(void 0)))
      .pipe(
        map(() => void 0)
      );
  }
}
